// src/v3/ui/backtester/strategy-editor.tsx
// REQ-8/9/10 (v1.1): редактор кода стратегии + ИИ-генерация.

import { Code2, Sparkles, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, Modal, useToast } from "../_common";
import { STRATEGY_EDITOR_MODE_OBJ, STRATEGY_SOURCE_OBJ } from "../_deps";
import type { StrategyInfo } from "./backtester.hooks";

interface StrategyEditorProps {
  open: boolean;
  onClose: () => void;
  // Если editing — стратегия для редактирования. Если null — создание новой.
  editing: StrategyInfo | null;
  // Builtin-стратегия для клонирования (если cloning=true).
  cloning: StrategyInfo | null;
  createStrategy: (input: {
    strategyId: string;
    name: string;
    description: string;
    code: string;
  }) => Promise<{ ok: true } | { ok: false; error: string }>;
  updateStrategy: (
    id: string,
    input: {
      strategyId: string;
      name?: string;
      description?: string;
      code?: string;
    },
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  deleteStrategy: (
    id: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  cloneStrategy: (
    id: string,
    newId: string,
    newName: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  generateStrategyCode: (
    description: string,
  ) => Promise<{ ok: true; code: string } | { ok: false; error: string }>;
  onSaved: () => void;
}

const STRATEGY_TEMPLATE = `import type { RuntimeStrategy } from "../domain";

export const myStrategy: RuntimeStrategy = {
  meta: { name: "my-strategy", description: "My strategy", version: "1.0.0" },
  universe: { symbols: ["AAPL"], warmupBars: 50 },
  execution: {
    initialCapital: 1_000_000,
    commission: { type: "percent", value: 0.1 },
    slippage: { type: "percent", value: 0.05 },
    maxHoldBars: 20,
    sizingMode: "fixed",
    cashAmount: 10_000,
  },
  params: {},
  entry: {
    condition: (ctx) => {
      // TODO: implement entry logic
      return null;
    },
  },
  exit: {
    condition: (ctx) => {
      // TODO: implement exit logic
      return null;
    },
  },
};

export default myStrategy;
`;

export function StrategyEditor({
  open,
  onClose,
  editing,
  cloning,
  createStrategy,
  updateStrategy,
  deleteStrategy,
  cloneStrategy,
  generateStrategyCode,
  onSaved,
}: StrategyEditorProps) {
  const { showToast } = useToast();
  const [strategyId, setStrategyId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState(STRATEGY_TEMPLATE);
  const [aiDescription, setAiDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const mode = cloning
    ? STRATEGY_EDITOR_MODE_OBJ.CLONE
    : editing
      ? STRATEGY_EDITOR_MODE_OBJ.EDIT
      : STRATEGY_EDITOR_MODE_OBJ.CREATE;

  // Инициализация полей при открытии/смене цели.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setStrategyId(editing.id);
      setName(editing.name);
      setDescription(editing.description);
      setCode(editing.code ?? STRATEGY_TEMPLATE);
    } else if (cloning) {
      setStrategyId(`${cloning.id}-copy`);
      setName(`${cloning.name} (copy)`);
      setDescription(cloning.description);
      setCode(STRATEGY_TEMPLATE);
    } else {
      setStrategyId("");
      setName("");
      setDescription("");
      setCode(STRATEGY_TEMPLATE);
    }
    setAiDescription("");
  }, [open, editing, cloning]);

  const handleSave = async () => {
    if (!strategyId.trim() || !name.trim() || !code.trim()) {
      showToast("error", "strategyId, name and code are required");
      return;
    }
    setIsSaving(true);
    try {
      if (mode === STRATEGY_EDITOR_MODE_OBJ.EDIT && editing) {
        const result = await updateStrategy(editing.id, {
          strategyId: editing.id,
          name,
          description,
          code,
        });
        if (!result.ok) {
          showToast("error", result.error);
          return;
        }
        showToast("success", "Strategy updated");
      } else if (mode === STRATEGY_EDITOR_MODE_OBJ.CLONE && cloning) {
        const result = await cloneStrategy(cloning.id, strategyId, name);
        if (!result.ok) {
          showToast("error", result.error);
          return;
        }
        // После клонирования — обновим код, если юзер изменил template.
        if (code !== STRATEGY_TEMPLATE) {
          await updateStrategy(strategyId, {
            strategyId,
            name,
            description,
            code,
          });
        }
        showToast("success", "Strategy cloned");
      } else {
        const result = await createStrategy({
          strategyId,
          name,
          description,
          code,
        });
        if (!result.ok) {
          showToast("error", result.error);
          return;
        }
        showToast("success", "Strategy created");
      }
      onSaved();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!aiDescription.trim()) {
      showToast("error", "Describe your strategy idea first");
      return;
    }
    setIsGenerating(true);
    try {
      const result = await generateStrategyCode(aiDescription);
      if (!result.ok) {
        showToast("error", result.error);
        return;
      }
      setCode(result.code);
      showToast("success", "Code generated — review and save");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!confirm(`Delete strategy "${editing.name}"? This cannot be undone.`)) {
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteStrategy(editing.id);
      if (!result.ok) {
        showToast("error", result.error);
        return;
      }
      showToast("success", "Strategy deleted");
      onSaved();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const title =
    mode === STRATEGY_EDITOR_MODE_OBJ.EDIT
      ? `Edit: ${editing?.name ?? ""}`
      : mode === STRATEGY_EDITOR_MODE_OBJ.CLONE
        ? `Clone: ${cloning?.name ?? ""}`
        : "New Strategy";

  const canEditId = mode === STRATEGY_EDITOR_MODE_OBJ.CREATE;
  const canDelete =
    mode === STRATEGY_EDITOR_MODE_OBJ.EDIT && editing?.source === STRATEGY_SOURCE_OBJ.USER;

  return (
    <Modal
      open={open}
      onClose={() => !isSaving && !isGenerating && onClose()}
      title={title}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              Strategy ID (kebab-case)
            </label>
            <input
              type="text"
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              disabled={!canEditId || isSaving}
              placeholder="my-strategy"
              className="w-full rounded-md border px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSaving}
            className="w-full rounded-md border px-3 py-2 text-sm"
          />
        </div>

        {/* REQ-10 (v1.1): ИИ-генерация кода */}
        <div className="rounded-md border border-violet-300 bg-violet-50/50 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-violet-700">
            <Sparkles className="h-4 w-4" />
            AI Code Generation
          </div>
          <textarea
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            disabled={isGenerating}
            placeholder="Describe your trading idea: e.g. 'Buy when RSI < 30 and price above SMA 50, sell when RSI > 70'"
            className="w-full min-h-[60px] rounded-md border px-3 py-2 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleGenerate()}
            disabled={isGenerating || !aiDescription.trim()}
          >
            <Sparkles className="h-3 w-3 mr-1" />
            {isGenerating ? "Generating…" : "Generate Code"}
          </Button>
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
            <Code2 className="h-3 w-3" />
            Strategy Code (TypeScript)
          </label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isSaving}
            className="w-full min-h-[300px] rounded-md border p-3 text-xs font-mono"
            spellCheck={false}
          />
        </div>

        <div className="flex justify-between gap-2">
          <div>
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleDelete()}
                disabled={isDeleting || isSaving}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                {isDeleting ? "Deleting…" : "Delete"}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={isSaving || isGenerating}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => void handleSave()}
              disabled={
                isSaving ||
                isGenerating ||
                !strategyId.trim() ||
                !name.trim() ||
                !code.trim()
              }
            >
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { StrategyEditor as StrategyEditorModal };
